(ns arthur.events.playback
  "Transport events.

  Named for intent rather than for the field they happen to set: `::toggle` is
  not `set-playing?`, because what the button means is \"start or stop\", and the
  resulting boolean is a consequence.

  NO GLOBAL INTERCEPTORS ON ::tick. At 30fps a spec-validating `after` or
  `std-interceptors/debug`'s `clojure.data/diff` would be thirty full-db
  traversals a second, which is the one genuinely expensive thing you can do to
  a small app-db. If global interceptors are added later they are added to a
  chain these events are excluded from, not to `reg-global-interceptor`."
  (:require [arthur.audio.mix :as mix]
            [arthur.clock :as clock]
            [arthur.domain.clip :as clip]
            [arthur.db :as app-db]
            [arthur.footage.store :as footage]
            [re-frame.core :as rf]))

(defn- fps [db] (get-in db [:clip :fps]))

(defn frames
  "The open symbol's length. Derived from the document every time, never kept in
  the db beside it, because an edit can change it."
  [db]
  (or (some-> (footage/entry (:clip/current db)) :clip
              (clip/frames (get-in db [:ui :open])))
      1))

(defn show
  "Put loaded clip `id` on screen, open on the symbol it opens on, with the
  playhead home. What every way of loading a document ends in, so that none of
  them can forget which symbol is open."
  [db id]
  (let [entry (footage/entry id)
        sid   (clip/opens-on (:clip entry))]
    (-> db
        (assoc :clip/current id
               :clip (select-keys entry [:fps :width :height :audio]))
        (update :ui merge
                {:open sid :tabs (if sid [sid] [])
                 ;; The layers switched off were another document's, and their
                 ;; ids mean nothing in this one. Whether tracing shows at all,
                 ;; and how strongly, is the person's and carries over.
                 :tracing (assoc (get-in db [:ui :tracing] app-db/tracing) :hidden #{})})
        ;; Occurrence addresses belong to the document being left. Creation is
        ;; derived from the primary selection, so carrying one across documents
        ;; could otherwise make a coincidentally equal id a nested destination.
        (update :ui dissoc :selection :selections :points)
        (assoc-in [:playback :frame] 0)
        (assoc-in [:playback :playing?] false))))

(rf/reg-event-fx
 ::tick
 (fn [{:keys [db]} [_ f]]
   ;; Written from the rAF loop when the DERIVED frame changes — not every
   ;; animation frame, and never as the thing the blit waits on. The picture is
   ;; painted from the clock directly; this only brings the document's idea of
   ;; the playhead up to date so the readout and the scrubber agree with it.
   (if (= f (get-in db [:playback :frame]))
     {:db db}
     (cond-> {:db (assoc-in db [:playback :frame] f)}
       (get-in db [:ui :gesture :auto-key?])
       (assoc :dispatch [:arthur.events.ui/record-gesture f])))))

(defn bounds
  "The frames playback runs between, both included: the open tab's in and out
  marks, or the whole symbol where one is not set."
  [db]
  (let [n (frames db)
        {:keys [in out]} (get-in db [:playback :range (get-in db [:ui :open])])]
    [(or in 0) (min (dec n) (or out (dec n)))]))

(defn- start
  "Play from the playhead, wherever it is — before the in mark or past the out
  — or from the in mark when it is on the last frame. Playing the last frame
  alone is a twenty-fourth of a second and then silence, so play there looked
  like a button that did nothing."
  [db]
  (let [n (frames db)
        [lo] (bounds db)
        f (get-in db [:playback :frame] 0)
        f (if (< f (dec n)) f lo)]
    {:db (update db :playback assoc :playing? true :frame f)
     ::play-from! [(fps db) n f]}))

(rf/reg-event-fx ::play (fn [{:keys [db]} _] (start db)))

(rf/reg-event-fx
 ::pause
 (fn [{:keys [db]} _]
   {:db (assoc-in db [:playback :playing?] false)
    ::pause! nil}))

(rf/reg-event-fx
 ::toggle
 (fn [{:keys [db]} _]
   (if (get-in db [:playback :playing?])
     {:db (assoc-in db [:playback :playing?] false) ::pause! nil}
     (start db))))

(rf/reg-event-fx
 ::seek
 (fn [{:keys [db]} [_ f]]
   (let [f (-> f (max 0) (min (dec (frames db))))]
     {:db (assoc-in db [:playback :frame] f)
      ::seek! [(fps db) (frames db) f]})))

(rf/reg-event-fx
 ::step
 (fn [{:keys [db]} [_ delta]]
   {:fx [[:dispatch [::seek (+ (get-in db [:playback :frame]) delta)]]]}))

(rf/reg-event-db
 ::mark
 ;; Resolve's I and O, on the open tab. A mark the other one has passed goes,
 ;; rather than leaving a range that ends before it starts.
 (fn [db [_ which]]
   (let [f (get-in db [:playback :frame] 0)]
     (update-in db [:playback :range (get-in db [:ui :open])]
                (fn [{:keys [in out] :as marks}]
                  (case which
                    :in  (cond-> (assoc marks :in f) (and out (< out f)) (dissoc :out))
                    :out (cond-> (assoc marks :out f) (and in (< f in)) (dissoc :in))))))))

(rf/reg-event-db
 ::drag-mark
 ;; A mark's handle dragged on the ruler: held short of the other mark rather
 ;; than pushing it off, as a key does.
 (fn [db [_ which f]]
   (update-in db [:playback :range (get-in db [:ui :open])]
              (fn [{:keys [in out] :as marks}]
                (case which
                  :in  (assoc marks :in (cond-> f out (min out)))
                  :out (assoc marks :out (cond-> f in (max in))))))))

(rf/reg-event-db
 ::clear-mark
 ;; ⇧I and ⇧O. With neither mark left, the tab plays as it did before marks.
 (fn [db [_ which]]
   (let [path [:playback :range (get-in db [:ui :open])]
         marks (dissoc (get-in db path) which)]
     (if (seq marks) (assoc-in db path marks) (update-in db (pop path) dissoc (peek path))))))

(rf/reg-event-fx
 ::set-rate
 (fn [{:keys [db]} [_ r]]
   {:db (assoc-in db [:playback :rate] r)
    ::rate! r}))

;; --- effects: every DOM touch on the audio element is one of these ---

(rf/reg-fx ::play!  (fn [_] (clock/play!)))
(rf/reg-fx ::play-from!
  (fn [[fps frames f]]
    ;; The transport is the audio clock; app-db's frame is the visible
    ;; playhead. Re-anchor before starting so Play always honors the frame
    ;; currently shown, including after the clock has reached the clip end.
    (clock/seek! fps frames f)
    (clock/play!)))
(rf/reg-fx ::pause! (fn [_] (clock/pause!)))
(rf/reg-fx ::rate!  (fn [r] (clock/set-rate! r)))
(rf/reg-fx ::seek!  (fn [[fps frames f]] (clock/seek! fps frames f)))
(rf/reg-fx ::loop!  (fn [on?] (clock/set-loop! on?)))
(rf/reg-fx ::mute!  (fn [on?] (clock/set-muted! on?)))

(rf/reg-event-fx
 ::toggle-loop
 (fn [{:keys [db]} _]
   (let [on? (not (get-in db [:playback :loop?]))]
     {:db (assoc-in db [:playback :loop?] on?) ::loop! on?})))

(rf/reg-event-fx
 ::toggle-mute
 (fn [{:keys [db]} _]
   (let [on? (not (get-in db [:playback :muted?]))]
     {:db (assoc-in db [:playback :muted?] on?) ::mute! on?})))

(rf/reg-event-fx
 ::select-clip
 (fn [{:keys [db]} [_ id]]
   ;; Changing the clip changes the resolver, the frame count and the rate all
   ;; at once, so the playhead goes home rather than being left pointing at a
   ;; frame the new clip may not have.
   (let [{:keys [label cid]} (footage/entry id)
         db (-> (show db id)
                ;; The document's identity goes with it. A built-in scene has no
                ;; project on the server, so this CLEARS the id rather than
                ;; keeping the last one — saving a fixture must create a
                ;; document of its own, not overwrite whatever was open before.
                (assoc :project {:id nil :cid cid :name label
                                 :seq nil :busy? false
                                 :status "built-in example · not a saved project"}))]
     {:db db
      ::pause! nil
      ::seek! [(fps db) (frames db) 0]
      ::clock! {:id id :sid (get-in db [:ui :open])}})))

;; ---------------------------------------------------------------------------
;; tabs
;;
;; A tab is an open symbol. `:open` is the one on screen and `:tabs` the ones
;; beside it, in the order they were opened. Switching is `:open` and a clock:
;; the stage, the timeline rows, the transport's length and a new shape's home
;; all follow `:open` through their subscriptions without being told.

(defonce ^:private clock-url (atom nil))

(rf/reg-fx
 ::clock!
 ;; WHAT THE OPEN SYMBOL SOUNDS LIKE, fetched once per thing that can change it:
 ;; a document opening, a tab switch, an edit to a track. The graph backend is
 ;; handed the buffer; the element backend needs a URL, which means a WAV, which
 ;; is the whole of what this used to cost.
 (fn [{:keys [id sid]}]
   (let [{:keys [clip audio store]} (footage/entry id)]
     (-> (if (clock/graph?)
           (mix/clock-source! clip sid audio store)
           (.then (mix/clock! clip sid audio store) (fn [u] {:url u})))
         (.then #(rf/dispatch [::clock-ready id sid %]))
         (.catch #(js/console.error %))))))

(rf/reg-event-fx
 ::clock-ready
 (fn [{:keys [db]} [_ id sid {:keys [url buffer seconds]}]]
   (if-not (and (= id (:clip/current db)) (= sid (get-in db [:ui :open])))
     {}
     (if (clock/graph?)
       (do
         (clock/attach-buffer! buffer seconds)
         ;; RATE, LOOP AND MUTE ARE RE-APPLIED. They are transport state and
         ;; outlive the clock they were set on, but a fresh graph starts at its
         ;; defaults — so without this a tab switch would quietly drop the take
         ;; back to 1x, unlooped and unmuted. The element kept them because they
         ;; were properties of a node that survived its own `src` changing.
         {:fx [[::rate! (get-in db [:playback :rate] 1.0)]
               [::loop! (boolean (get-in db [:playback :loop?]))]
               [::mute! (boolean (get-in db [:playback :muted?]))]
               [::seek! [(fps db) (frames db) (get-in db [:playback :frame] 0)]]
               ;; An edit under a playing take resumes it. The element stopped
               ;; dead here, which made adjusting a fade while listening to it
               ;; a thing you could only do once.
               (when (get-in db [:playback :playing?]) [::play! nil])]})
       (do
         ;; A blob URL made for the last tab is released when the next one lands,
         ;; and never the document's own file.
         (when-let [old @clock-url]
           (when (not= old url) (js/URL.revokeObjectURL old)))
         (reset! clock-url (when (and (not= url (:audio (footage/entry id)))
                                      (.startsWith url "blob:"))
                             url))
         {:db (assoc-in db [:clip :audio] url)})))))

(rf/reg-event-db
 ::paint-failed
 (fn [db [_ message]]
   (update db :project merge {:status (str "cannot draw: " message)})))

(rf/reg-event-fx
 ::refresh-clock
 ;; After an edit that changes what the open symbol sounds like.
 (fn [{:keys [db]} _]
   {::clock! {:id (:clip/current db) :sid (get-in db [:ui :open])}}))

(defn openable?
  "Can symbol `sid` be opened as a tab? It has to exist — and a tracing symbol
  has no inside to open: it is footage, which is seen by placing it."
  [db sid]
  (let [clip (:clip (footage/entry (:clip/current db)))]
    (boolean (and (clip/symbol clip sid) (not (clip/trace? (clip/symbol clip sid)))))))

(defn open
  "`db` with symbol `sid` the open tab, stopped at its first frame."
  [db sid]
  (-> db
      (update-in [:ui :tabs] #(if (some #{sid} %) % (conj (vec %) sid)))
      (assoc-in [:ui :open] sid)
      ;; A node address is relative to the tab being left. Opening a symbol
      ;; therefore arrives at its root; a pool `[:symbol _]` selection is not an
      ;; occurrence address and may survive.
      (update :ui (fn [ui]
                    (cond-> ui
                      (= :node (first (:selection ui)))
                      (dissoc :selection :selections))))
      (assoc-in [:playback :frame] 0)
      (assoc-in [:playback :playing?] false)))

(rf/reg-event-fx
 ::open-symbol
 (fn [{:keys [db]} [_ sid]]
   (if (or (not (openable? db sid)) (= sid (get-in db [:ui :open])))
     {}
     {:db (open db sid)
      ::pause! nil
      ::clock! {:id (:clip/current db) :sid sid}})))

(rf/reg-event-fx
 ::close-tab
 (fn [{:keys [db]} [_ sid]]
   ;; The last tab cannot be closed: something is always on screen, because the
   ;; transport and the stage have no meaning without a symbol.
   (let [tabs (get-in db [:ui :tabs])
         left (vec (remove #{sid} tabs))]
     (cond
       (empty? left) {}
       (not= sid (get-in db [:ui :open])) {:db (assoc-in db [:ui :tabs] left)}
       :else (let [i (.indexOf tabs sid)]
               {:db (assoc-in db [:ui :tabs] left)
                :dispatch [::open-symbol (get left (min i (dec (count left))))]})))))

(rf/reg-event-db
 ::frame-palette
 ;; Written by the player as it paints: see `subs.render/frame-palette`.
 (fn [db [_ active]] (assoc-in db [:ui :frame-palette (get-in db [:ui :open])] active)))
